import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import type { Car } from '@/api/cars/types';
import { useCarSubscriptionEarnings } from '@/api/partners/cars/use-car-subscription-earnings';
import { Card, Text } from '@/ui';
import { CheckItem } from '@/ui/check-item';
import { Loader } from '@/ui/loader';

import type { CarDetailsFormType } from '../car-details-form';
import { SubscriptionEarningsCard } from '../subscription-earnings-card';

const faqSections = [
  {
    heading: 'How Subscriptions earn',
    paragraphs: [
      "Subscriptions earn you a base rate that includes the first 1,000 miles driven each month. Beyond that, you're paid out in tiers of 500-mile increments, so additional mileage is always rounded up to the next tier.",
      'On a per-mile basis, guests effectively pay more for miles on a subscription than on a standard daily rental. Subscriptions are priced to stay profitable no matter how much a guest drives, covering your financing, maintenance, and insurance costs.',
    ],
  },
  {
    heading: 'Canceling an active subscription',
    paragraphs: [
      'To cancel while a guest is in your car, notify us at least 15 days before your requested cancellation date.',
      "If you cancel within 15 days, you'll forfeit your final earnings segment for that subscription.",
    ],
  },
  {
    heading: 'Estimated earnings',
    paragraphs: [
      'Typical mileage is 500 miles/week or 1,500/month. Earnings vary based on actual usage.',
      "Your mileage tier rounds up to the next highest tier. For example, if a guest drives 1,200 miles in a month, you'll earn at the 1,500-mile tier.",
    ],
  },
  {
    heading: 'How are earnings calculated?',
    paragraphs: [
      'Subscriptions offer consistent returns with minimal effort. Expect at least 20% ROI monthly. Some setups see 50%+ returns, especially if the vehicle is leased or already used. Your subscriptions returns are based on a 20% additional return factoring in the cost of a brand new vehicle lease, miles used, and insurance. If your vehicle is slightly older, is financed or purchased, and already has some miles on it, your returns will be even better.',
    ],
  },
];

const carSubscriptionFormSchema = z.object({
  allowSubscription: z.boolean().optional(),
});

export type CarSubscriptionFormType = z.infer<typeof carSubscriptionFormSchema>;

export default function CarSubscriptionForm({
  car,
  name,
  onChange,
  onValidChange,
}: {
  car: Car;
  name: string;
  onChange: (name: string, values: CarDetailsFormType) => void;
  onValidChange: (name: string, isValid: boolean) => void;
}) {
  const { styles, theme } = useStyles(stylesheet);

  const {
    data: carSubscriptionEarnings,
    isPending: isLoadingCarSubscriptionEarnings,
  } = useCarSubscriptionEarnings({
    variables: { id: car.id },
  });

  const {
    control,
    trigger,
    setValue,
    getValues,
    formState: { isValid },
    watch,
  } = useForm<CarSubscriptionFormType>({
    defaultValues: {
      allowSubscription: car.allowSubscription,
    },
    resolver: zodResolver(carSubscriptionFormSchema),
  });

  useEffect(() => {
    onChange(name, getValues());
  }, []);

  useEffect(() => {
    onValidChange(name, isValid);
  }, [isValid]);

  useEffect(() => {
    const sub = watch(() => {
      onChange(name, getValues());
    });
    return () => sub.unsubscribe();
  }, [watch]);

  const allowSubscription = useWatch({
    control,
    name: 'allowSubscription',
  });

  if (isLoadingCarSubscriptionEarnings) {
    return (
      <View style={{ flex: 1, backgroundColor: theme.colors.background_1 }}>
        <View
          style={{
            width: '100%',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Loader style={{ height: 250, width: 250 }} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.contentContainer}>
        <View style={styles.subscriptionContainer}>
          <View style={{ gap: 16 }}>
            <Text type="title2">Subscription</Text>

            <CheckItem
              text="Allow Subscriptions"
              caption="Enable this to allow guests to subscribe to your car for extended periods. Weekly, monthly, or up to 6 months. Ideal for long-term rentals and recurring revenue."
              onPress={() => {
                setValue('allowSubscription', !allowSubscription);
              }}
              active={allowSubscription}
              type="checkbox"
            />
          </View>

          <SubscriptionEarningsCard
            earnings={carSubscriptionEarnings}
            style={styles.earningsCard}
          />
        </View>

        <View style={styles.faqContainer}>
          <Card style={{ padding: 16, gap: 16 }}>
            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              Subscription FAQ
            </Text>

            <View style={{ gap: 16 }}>
              <Text type="title2">What Are Eon Subscriptions</Text>

              <View style={{ gap: 16 }}>
                {faqSections.map((section) => {
                  return (
                    <View key={section.heading} style={{ gap: 8 }}>
                      <Text type="bodyRegular">{section.heading}</Text>

                      {section.paragraphs.map((paragraph) => {
                        return (
                          <Text
                            key={paragraph}
                            type="bodyRegular"
                            style={{ color: theme.colors.text.secondary }}
                          >
                            {paragraph}
                          </Text>
                        );
                      })}
                    </View>
                  );
                })}
              </View>
            </View>
          </Card>
        </View>
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    paddingTop: 24,
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 16,
    gap: 16,
  },
  contentContainer: {
    flexGrow: 1,
    gap: 32,
    justifyContent: {
      xs: 'space-between',
      md: 'flex-start',
    },
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    paddingBottom: 32,
  },
  subscriptionContainer: {
    gap: 16,
    flex: {
      md: 1,
    },
  },
  earningsCard: {
    flex: {
      md: 1,
    },
  },
  faqContainer: {
    flex: {
      md: 1,
    },
  },
}));
